/**
 * Edit Offer Page - Simplified offer editing
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { useOffer } from '@/contexts/OfferContext';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/shared/LoadingState';
import { ArrowLeft, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import { OfferBackgroundStyle } from '@/types';
import { OFFER_BACKGROUNDS } from '@/components/offers/OfferModal';

export default function EditOfferPage() {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const { getOffer, updateOffer } = useOffer();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [originalImageURL, setOriginalImageURL] = useState<string>('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discountText: '',
    backgroundStyle: 'purple' as OfferBackgroundStyle,
    startDate: '',
    endDate: '',
    isActive: true,
  });

  useEffect(() => {
    loadOffer();
  }, [offerId]);

  const loadOffer = async () => {
    if (!offerId) {
      navigate('/offers');
      return;
    }

    setLoading(true);
    try {
      const offer = await getOffer(offerId);
      if (!offer) {
        toast.error('Oferta no encontrada');
        navigate('/offers');
        return;
      }

      setFormData({
        title: offer.title,
        description: offer.description,
        discountText: offer.discountText || '',
        backgroundStyle: offer.backgroundStyle || 'purple',
        startDate: formatDateForInput(offer.startDate),
        endDate: formatDateForInput(offer.endDate),
        isActive: offer.isActive,
      });

      if (offer.imageURL) {
        setOriginalImageURL(offer.imageURL);
        setImagePreview(offer.imageURL);
      }
    } catch (error) {
      console.error('Error loading offer:', error);
      toast.error('Error al cargar la oferta');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toISOString().slice(0, 16);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede ser mayor a 5MB');
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setOriginalImageURL('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!offerId || !formData.title || !formData.description || !formData.discountText) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      toast.error('Por favor selecciona las fechas de inicio y fin');
      return;
    }

    setSaving(true);
    try {
      let imageURL = originalImageURL;

      // Upload new image if selected
      if (imageFile) {
        const timestamp = Date.now();
        const fileName = `offers/${offerId}/${timestamp}_${imageFile.name}`;
        const storageRef = ref(storage, fileName);

        await uploadBytes(storageRef, imageFile);
        imageURL = await getDownloadURL(storageRef);
      }

      const startDate = Timestamp.fromDate(new Date(formData.startDate));
      const endDate = Timestamp.fromDate(new Date(formData.endDate));

      await updateOffer(offerId, {
        title: formData.title,
        description: formData.description,
        discountText: formData.discountText,
        backgroundStyle: formData.backgroundStyle,
        imageURL: imageURL || undefined,
        startDate,
        endDate,
        isActive: formData.isActive,
      });

      toast.success('Oferta actualizada exitosamente');
      navigate('/offers');
    } catch (error) {
      console.error('Error updating offer:', error);
      toast.error('Error al actualizar la oferta');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingState message="Cargando oferta..." />;
  }

  return (
    <div className="min-h-screen bg-surface-2">
      <div className="container mx-auto p-6 max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/offers')}
            variant="ghost"
            className="p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-ink">
              Editar Oferta
            </h1>
            <p className="text-ink-muted">
              Modifica los detalles de la oferta
            </p>
          </div>
        </div>

        {/* Preview Card */}
        <div className="mb-8">
          <p className="text-sm font-medium text-ink-muted mb-3">
            Vista Previa
          </p>
          <div className={`relative overflow-hidden rounded-2xl ${OFFER_BACKGROUNDS[formData.backgroundStyle].gradient} p-6 text-white shadow-xl`}>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>

            <div className="relative flex items-center gap-6">
              <div className="flex-shrink-0">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Oferta"
                    className="w-24 h-24 object-contain rounded-xl bg-white/20 p-2"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-xl bg-white/20 flex items-center justify-center">
                    <span className="text-5xl">🎉</span>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-bold mb-1 truncate">
                  {formData.title || 'Título de la oferta'}
                </h3>
                <p className="text-white/90 text-sm mb-3 line-clamp-2">
                  {formData.description || 'Descripción de la oferta...'}
                </p>
                <div className="inline-block px-4 py-2 bg-white/20 rounded-full font-bold text-lg backdrop-blur-sm">
                  {formData.discountText || '20%'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-surface rounded-lg shadow-sm p-6 space-y-6">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Imagen de la Oferta (opcional)
            </label>
            <p className="text-xs text-ink-subtle mb-3">
              Sube una imagen o logo para mostrar en la oferta.
            </p>

            {imagePreview ? (
              <div className="relative inline-block">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-32 h-32 object-contain rounded-lg border-2 border-hairline bg-surface-2"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-danger/100 text-white p-1 rounded-full hover:bg-danger/90 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed border-hairline rounded-lg cursor-pointer hover:border-accent/30 dark:hover:border-accent/30 transition bg-surface-2">
                <ImageIcon className="w-8 h-8 text-ink-subtle mb-2" />
                <span className="text-xs text-ink-subtle">Subir imagen</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Background Style Selector */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Estilo de Fondo
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
              {(Object.keys(OFFER_BACKGROUNDS) as OfferBackgroundStyle[]).map((style) => (
                <button
                  key={style}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, backgroundStyle: style }))}
                  className={`relative h-14 rounded-lg ${OFFER_BACKGROUNDS[style].gradient} transition-all ${
                    formData.backgroundStyle === style
                      ? 'ring-2 ring-offset-2 ring-accent scale-105'
                      : 'hover:scale-105 opacity-80 hover:opacity-100'
                  }`}
                  title={OFFER_BACKGROUNDS[style].label}
                >
                  {formData.backgroundStyle === style && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white text-lg">✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-subtle mt-2">
              Fondo seleccionado: {OFFER_BACKGROUNDS[formData.backgroundStyle].label}
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Título *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              maxLength={50}
              className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-accent"
              placeholder="Ej: Black Friday, Verano 2024..."
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Descripción *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={3}
              maxLength={200}
              className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-accent"
              placeholder="Ej: Disfruta de un descuento exclusivo en tu corte favorito"
            />
          </div>

          {/* Discount Text */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              Texto del Descuento *
            </label>
            <input
              type="text"
              name="discountText"
              value={formData.discountText}
              onChange={handleChange}
              required
              maxLength={100}
              className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-accent"
              placeholder="Ej: 20% de descuento en todos los servicios"
            />
            <p className="text-xs text-ink-subtle mt-1">
              Este texto se muestra destacado en la oferta
            </p>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Fecha de inicio *
              </label>
              <input
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Fecha de fin *
              </label>
              <input
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="w-4 h-4 text-accent rounded focus:ring-accent"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-ink-muted">
              Oferta activa
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t border-hairline">
            <Button
              type="button"
              onClick={() => navigate('/offers')}
              variant="outline"
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-accent hover:bg-accent/90 text-on-accent"
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
